import { Modal } from "antd";
import { EquipmentSurveyInput } from "gql/graphql";
import useAlert from "hooks/useAlert";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { UseMutateFunction } from "react-query";
import { mapCSVToEquipmentSurvey } from "./EquipmentUploadModal.helper";
import styles from "./EquipmentUploadModal.module.scss";
import EquipmentUploadModalFooter from "./EquipmentUploadModalFooter";

interface EquipmentUploadModalProps {
    modalIsOpen: boolean;
    onCancel: () => void;
    submitEquipmentSurvey: UseMutateFunction<boolean | null | undefined, unknown, EquipmentSurveyInput, unknown>;
}

interface UploadFile {
    file: File;
    description?: string;
}

function EquipmentUploadModal({ modalIsOpen, onCancel, submitEquipmentSurvey }: EquipmentUploadModalProps) {
    const buildingModelUid = useBuildingModelUid();
    const alert = useAlert();

    const [files, setFiles] = useState<UploadFile[]>([]);
    const [parsedEquipment, setParsedEquipment] = useState<EquipmentSurveyInput | null>(null);

    if (!buildingModelUid) throw new Error("Missing buildingModel");

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const onDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        const text = await file.text();

        const parsedCSV = parseCSV(text);

        setParsedEquipment(mapCSVToEquipmentSurvey(parsedCSV));

        const newFiles = acceptedFiles.map((file) => ({
            file,
            description: "",
        }));
        setFiles(newFiles);
    };

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
        accept: { "text/csv": [".csv"] },
        maxFiles: 1,
    });

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const modalSubmitHandler = () => {
        if (!parsedEquipment) return;

        submitEquipmentSurvey(parsedEquipment);
        alert.success("Your CSV is being submitted. Please wait while you are redirected to the building page.");
        setFiles([]);
        onCancel();
    };

    const handleCancel = () => {
        setFiles([]);
        onCancel();
    };

    const parseCSV = (text: string): Record<string, string> => {
        const lines = text.trim().split("\n");
        const headers = lines[0].split(",");
        const values = lines[1].split(",");

        return headers.reduce(
            (obj, header, i) => ({
                ...obj,
                [header.trim()]: values[i]?.trim() || "",
            }),
            {} as Record<string, string>
        );
    };

    return (
        <Modal
            open={modalIsOpen}
            onCancel={handleCancel}
            title="Upload equipment csv"
            footer={
                <EquipmentUploadModalFooter
                    onSave={() => modalSubmitHandler()}
                    onCancel={onCancel}
                    isLoading={false}
                    disabled={!parsedEquipment && !files}
                />
            }
        >
            <div
                {...getRootProps()}
                className={styles.dropzone}
                style={{
                    backgroundColor: isDragActive ? "#e6f7ff" : "transparent",
                }}
            >
                <input {...getInputProps()} />
                {isDragActive ? <p>Drop the files here...</p> : <p>Drag & drop csv file, or click to select it.</p>}
            </div>

            {fileRejections.length > 0 && (
                <div className={styles.error}>
                    {fileRejections.map(({ file }) => (
                        <p key={file.name}>{file.name}: File exceeds 20GB limit</p>
                    ))}
                </div>
            )}

            {files.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                    <p style={{ fontWeight: 500, marginBottom: "8px" }}>Selected file (maximum of 1 file)</p>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {files.map((item, index) => (
                            <li key={item.file.name + item.file.lastModified} className={styles.uploadedFile}>
                                <span>{item.file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(files.indexOf(item))}
                                    className={styles.removeButton}
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    );
}

export default EquipmentUploadModal;
